// The filter data-type visual system — one leading glyph + signature color per
// sphere-property data type, mirroring the context-selector design
// (docs/design/context-selector/filter-row.jsx `EF_TYPES`). Shared by every
// filter surface (EditableFilterRow, FilterEditorPanel, PropertyFilter) so a
// given type reads identically wherever it appears.
//
// Colors are NOT declared here — they live as CSS custom properties in
// `styles/_filter-type-tokens.scss` (light + dark). This module only maps the
// platform's `PropertyType` union to the PrimeIcons glyph and the token
// `modifier` that selects the color (via the `.filter-type-icon--<modifier>`
// utility class, or the `--filter-type-<modifier>` var for accent chrome).
import type { PropertyType } from '../components/molecules/PropertyFilter/PropertyFilterMolecule';

export interface FilterTypeVisual {
  /** PrimeIcons glyph class (e.g. `pi-hashtag`), the closest platform icon to the mock's lucide name. */
  icon: string;
  /** Token key: selects `.filter-type-icon--<modifier>` and `var(--filter-type-<modifier>)`. */
  modifier: string;
}

/**
 * Platform `PropertyType` → glyph + color token. The mock's lucide names map to
 * the nearest PrimeIcons glyph (type→align-left, hash→hashtag, tags→tags,
 * calendar→calendar, toggle-right→check-square, git-branch→sitemap).
 */
const FILTER_TYPE_VISUALS: Record<PropertyType, FilterTypeVisual> = {
  text: { icon: 'pi-align-left', modifier: 'text' },
  numeric: { icon: 'pi-hashtag', modifier: 'number' },
  enum: { icon: 'pi-tags', modifier: 'select' },
  date: { icon: 'pi-calendar', modifier: 'date' },
  datetime: { icon: 'pi-calendar-clock', modifier: 'date' },
  boolean: { icon: 'pi-check-square', modifier: 'boolean' },
  urgency: { icon: 'pi-flag-fill', modifier: 'urgency' },
  arc: { icon: 'pi-sitemap', modifier: 'arc' },
  // Reuses the `reference` token/modifier already established by
  // `RelationshipLaneRow` (the pinned lane row) for this exact concept — a new
  // `--filter-type-relationship` token would just duplicate it under a second name.
  relationship: { icon: 'pi-share-alt', modifier: 'reference' },
  // Reuses the `multi` token/modifier (`--filter-type-multi`, violet) and
  // `pi-eye` glyph already established for this exact concept — a new
  // `--filter-type-perspective` token would just duplicate it.
  perspective: { icon: 'pi-eye', modifier: 'multi' },
  // A `reference` aspect points at another sphere — shares the `reference`
  // token/modifier with `relationship` above (same "points at another
  // sphere" semantic), distinguished by icon only.
  reference: { icon: 'pi-link', modifier: 'reference' },
  // The four unified-filters-v2 additions (EAINT-908 Phase 2) — each has its
  // own `--filter-type-<modifier>` token (`_filter-type-tokens.scss`), added
  // ahead of this map for exactly this wiring.
  multi: { icon: 'pi-tags', modifier: 'multi' },
  person: { icon: 'pi-user', modifier: 'person' },
  spheretype: { icon: 'pi-box', modifier: 'spheretype' },
  spherefamily: { icon: 'pi-th-large', modifier: 'spherefamily' },
};

const FALLBACK_VISUAL: FilterTypeVisual = { icon: 'pi-tag', modifier: 'select' };

const FILTER_TYPE_LABELS: Record<PropertyType, string> = {
  text: 'Text',
  numeric: 'Number',
  enum: 'Select',
  date: 'Date',
  datetime: 'Date & Time',
  boolean: 'Boolean',
  urgency: 'Urgency',
  arc: 'Arc',
  relationship: 'Relationship',
  perspective: 'Perspective',
  reference: 'Reference',
  multi: 'Multi-select',
  person: 'Person',
  spheretype: 'Sphere Type',
  spherefamily: 'Sphere Family',
};

/** Short human-readable label for a property type, e.g. "Date", "Boolean". */
export function getFilterTypeLabel(type?: PropertyType): string {
  return (type && FILTER_TYPE_LABELS[type]) || 'Property';
}

/** Resolve the glyph + color token for a property type (safe on unknown types). */
export function getFilterTypeVisual(type?: PropertyType): FilterTypeVisual {
  return (type && FILTER_TYPE_VISUALS[type]) || FALLBACK_VISUAL;
}

/** Full `className` for a leading type glyph, e.g. `pi pi-hashtag filter-type-icon filter-type-icon--number`. */
export function getFilterTypeIconClassName(type?: PropertyType): string {
  const { icon, modifier } = getFilterTypeVisual(type);
  return `pi ${icon} filter-type-icon filter-type-icon--${modifier}`;
}

/** The `--filter-type-<modifier>` CSS var for a type — used as an accent color for row/panel chrome. */
export function getFilterTypeAccentVar(type?: PropertyType): string {
  return `var(--filter-type-${getFilterTypeVisual(type).modifier})`;
}
